import React, { useState, useEffect } from 'react';
import { useMarquee } from '../components/Marquee';
import { useCart } from '../context/CartContext';
import { useSiteContent } from '../context/SiteContentContext';
import { trackConversionOnce, packEventData } from '../utils/fbConversions';
import { setCheckoutFlow } from '../utils/checkoutFlow';
import ProductGallery from '../components/ProductGallery';
import AboutMedia from '../components/AboutMedia';
import IngredientIcon from '../components/IngredientIcons';

const reviews = [
  { text: 'I lost 4 kg in just 3 weeks! This tea is truly amazing. I drink it every morning and stay energetic all day.', author: 'Zoya S.', city: 'Lahore' },
  { text: 'I was skeptical at first, but after 2 packs it clearly works. My metabolism has improved and cravings have reduced.', author: 'Ahmed K.', city: 'Karachi' },
  { text: 'The COD option is very convenient. Package arrived in 3 days and packaging was excellent. Results are amazing!', author: 'Fatima N.', city: 'Islamabad' },
  { text: "The taste is very good — not bitter at all. I expected it to be like regular green tea, but it's completely different!", author: 'Sara M.', city: 'Peshawar' },
];

const defaultIngredients = [
  { icon: '🍵', name: 'Unoxidized Tea', desc: 'Boosts metabolism and helps burn full-body fat naturally.' },
  { icon: '🌿', name: 'Fennel Seeds', desc: 'Clears digestion and reduces belly bloating for a flatter stomach.' },
  { icon: '🫚', name: 'Carom Seeds', desc: 'Targets stubborn belly fat and speeds up your weight loss.' },
  { icon: '🌱', name: 'Dried Mint', desc: 'Cools the body, stops extra hunger, and naturally detoxifies.' },
  { icon: '🟫', name: 'Ceylon Cinnamon', desc: 'Controls blood sugar to stop daily sweet cravings.' },
  { icon: '🌑', name: 'Nigella Seeds', desc: 'Builds strong daily immunity and improves overall health.' },
  { icon: '💚', name: 'Cardamom', desc: 'Increases energy levels and speeds up the fat-burning process.' },
  { icon: '⚫', name: 'Black Pepper', desc: 'Helps your body absorb all herbs quickly for faster results.' },
];

const faqs = [
  { q: "What's in Organic Slim Fit Tea?", a: 'Our blend includes carefully selected herbs known for their metabolism-supporting and digestive properties — 100% natural, no artificial additives, no fillers, no hidden chemicals.' },
  { q: 'How soon will I see results?', a: 'Most people notice changes in digestion and energy within the first 1–2 weeks. For visible fat loss results, we recommend daily use for at least 4–6 weeks alongside a balanced diet.' },
  { q: 'How do I brew it?', a: 'Add one teaspoon of tea to a cup of freshly boiled water. Let it steep for 5–7 minutes, strain, and drink. Best enjoyed 30 minutes before or after a meal.' },
  { q: 'Is it safe to drink every day?', a: 'Yes. Made from natural herbs, it is safe for daily use for most healthy adults. If pregnant, nursing, or on medication, please check with your doctor first.' },
  { q: 'Can I use it as a replacement for diet or exercise?', a: "No herbal tea replaces a healthy diet and regular movement. Organic Slim Fit supports your body's natural processes so your efforts go further — a daily companion, not a substitute." },
];

const comparisonRows = [
  '100% Natural Ingredients',
  'No Laxatives or Senna',
  'No Artificial Flavors or Colors',
  'Supports Natural Fat Loss',
  'Safe for Daily Use',
  'No Side Effects',
  'Handcrafted, Small Batch',
  'Transparent Ingredients',
];

/* ── Limited-time offer countdown ─────────────────────────────
   Har expiry par timer ki muddat dobara RANDOM pick hoti hai:
   3 hours, 20 hours, 22 hours ya 1 day (24 hours).
   Sirf 1-second ka interval, chhota sa component — speed par zero asar. */
const OFFER_TIMER_KEY = 'osf_offer_expiry';
const OFFER_DURATIONS_H = [3, 20, 22, 24];
function nextOfferExpiry() {
  const h = OFFER_DURATIONS_H[Math.floor(Math.random() * OFFER_DURATIONS_H.length)];
  return Date.now() + h * 3600 * 1000;
}
function offerRemainingMs() {
  try {
    let expiry = Number(localStorage.getItem(OFFER_TIMER_KEY) || 0);
    if (!expiry || expiry <= Date.now()) {
      expiry = nextOfferExpiry();
      localStorage.setItem(OFFER_TIMER_KEY, String(expiry));
    }
    return Math.max(0, expiry - Date.now());
  } catch (_) { return 0; }
}
function OfferTimer() {
  const [ms, setMs] = useState(offerRemainingMs);
  useEffect(() => {
    const id = setInterval(() => setMs(offerRemainingMs()), 1000);
    return () => clearInterval(id);
  }, []);
  const totalSec = Math.floor(ms / 1000);
  const d = Math.floor(totalSec / 86400);
  const h = Math.floor((totalSec % 86400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n) => String(n).padStart(2, '0');
  const units = d > 0
    ? [[d, 'DAYS'], [h, 'HRS'], [m, 'MIN'], [s, 'SEC']]
    : [[h, 'HRS'], [m, 'MIN'], [s, 'SEC']];
  return (
    <div className="ph-timer" aria-label="Limited time offer ends in">
      <span className="ph-timer-label">⏳ Offer ends in</span>
      <span className="ph-timer-digits">
        {units.map(([v, lab], i) => (
          <span key={lab} className="ph-timer-cell">
            <b>{pad(v)}</b><i>{lab}</i>
            {i < units.length - 1 && <em>:</em>}
          </span>
        ))}
      </span>
    </div>
  );
}

export default function HomePage({ onOrderNow }) {
  const { addToCart, setCurrentPage, packs, delivery } = useCart();
  const { config: o, loading: cfgLoading } = useSiteContent();
  // The About/Elevate video's space is reserved from first paint — when Firestore arrives
  // section 'no-media' se 'about-wide' mein flip na ho (yehi CLS 0.273 ka sabab tha).
  // While the config loads, the wide layout stays with the media-box placeholder.
  const aboutHasMedia = Boolean(o?.about_media_url) || cfgLoading;
  const [selected, setSelected] = useState(1); // 1st pack (Rs.1,199) selected by default
  const [openFaq, setOpenFaq] = useState(0);
  // Reviews strip — JS marquee (har system par chalta hai, hover par pause)
  const reviewsTrackRef = useMarquee(45);

  const scrollToAnchor = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const packList = [
    { id: 1, name: packs.pack1.name, sub: packs.pack1.sub, price: packs.pack1.price, originalPrice: packs.pack1.original, badge: 'standard', delivery, saveBadge: null },
    { id: 2, name: packs.pack2.name, sub: packs.pack2.sub, price: packs.pack2.price, originalPrice: packs.pack2.original, badge: 'most-popular', delivery, saveBadge: o?.pack2_save_badge || 'Save More Rs.200' },
    { id: 3, name: packs.pack3.name, sub: packs.pack3.sub, price: packs.pack3.price, originalPrice: packs.pack3.original, badge: 'super-saver', delivery, saveBadge: o?.pack3_save_badge || 'Save More Rs.600' },
  ];

  // Hostinger-style discount math per pack: % off pill + absolute savings
  const dealFor = (p) => {
    const price = Number(p.price), orig = Number(p.originalPrice);
    const save = Math.max(0, orig - price);
    const pct = orig > 0 ? Math.round((save / orig) * 100) : 0;
    return { price, orig, save, pct };
  };

  const ingredients = (() => {
    const list = [];
    for (let n = 1; n <= 8; n++) {
      const name = o?.[`ing${n}_name`];
      const desc = o?.[`ing${n}_desc`];
      const icon = o?.[`ing${n}_icon`];
      if (name && desc) list.push({ icon: icon || defaultIngredients[n - 1]?.icon || '🌿', name, desc });
    }
    return list.length >= 4 ? list : defaultIngredients;
  })();

  const activePack = packList.find(p => p.id === selected) || packList[1];

  const quickBuy = () => {
    onOrderNow ? onOrderNow() : setCurrentPage('checkout');
    window.scrollTo({ top: 0 });
  };

  const handleAddToCart = () => {
    try { localStorage.setItem('osf_selected_pack', String(selected)); } catch (_) {}
    trackConversionOnce('AddToCart', `pack${selected}`, packEventData(activePack.name, activePack.price));
    addToCart(1, activePack.name, activePack.price, activePack.sub);
    setCurrentPage('cart');
    window.scrollTo({ top: 0 });
  };

  const handleBuyNow = () => {
    try { localStorage.setItem('osf_selected_pack', String(selected)); } catch (_) {}
    // Buy It Now = straight to checkout, so AddToCart also fires here (otherwise the campaign would never see AddToCart)
    trackConversionOnce('AddToCart', `pack${selected}`, packEventData(activePack.name, activePack.price));
    trackConversionOnce('InitiateCheckout', `pack${selected}`, packEventData(activePack.name, activePack.price));
    // Pack already chosen on the home page → checkout shows the classic
    // quantity-only view (no pack selector).
    setCheckoutFlow('buy');
    setCurrentPage('checkout');
    window.scrollTo({ top: 0 });
  };

  return (
    <>
      {/* SIMPLIFIED PRODUCT HERO — image + buy box (DailyTrade-style layout) */}
      <section className="ph-hero">
        <div className="ph-wrap">
          <div className="ph-gallery">
            <ProductGallery config={o} alt={o?.product_title || 'Organic Slim Fit Tea'} />
          </div>

          <div className="ph-info">
            <div className="ph-stars-line">
              <span className="ph-stars">★★★★★</span> { '10,000+'} Satisfied Customers
            </div>
            <h1 className="ph-title">{o?.product_title || 'Organic Slim Fit Tea'}</h1>
            <div className="ph-subtitle">{o?.product_subtitle || '130G | 65–70 Cups Per Pack'}</div>

            <div className="ph-price-row">
              <span className="ph-price-old">Rs.{Number(activePack.originalPrice).toLocaleString()}</span>
              <span className="ph-price-new">Rs.{Number(activePack.price).toLocaleString()}</span>
              <span className="ph-sale-pill">Sale</span>
            </div>

            <div className="ph-checklist">
              <div><span className="ph-tick">✓</span> Buy More &amp; Save More — Free Delivery All Over Pakistan</div>
              <div><span className="ph-tick">✓</span> 100% Herbal & Natural - No Side Effect</div>
              <div><span className="ph-tick">✓</span> Best For Weight Loss - No Extra Exercise &amp; Dieting Required</div>
            </div>

            <div className="ph-urgency">Limited Time Discount Offer - Limited Stock</div>
            <OfferTimer />

            <div className="ph-options">
              {packList.map(p => {
                const deal = dealFor(p);
                const ribbon = p.badge === 'most-popular' ? 'Most Popular' : p.badge === 'super-saver' ? 'Best Value' : null;
                const freeDelivery = p.id !== 1; // 60-day (2 packets) aur 90-day (3 packets) par FREE delivery
                return (
                  <div key={p.id} className={`ph-opt ${selected === p.id ? 'selected' : ''}`} onClick={() => setSelected(p.id)}>
                    {ribbon && <span className="ph-ribbon">{ribbon}</span>}
                    <div className="ph-radio" />
                    <div className="ph-opt-name">
                      {p.name}
                      {freeDelivery ? (
                        <span className="ph-free-delivery">FREE DELIVERY</span>
                      ) : (
                        <span className="ph-delivery-paid">DELIVERY RS.199</span>
                      )}
                    </div>
                    <div className="ph-opt-price">
                      <div className="new">Rs.{deal.price.toLocaleString()}</div>
                      {deal.orig > deal.price && <div className="old">Rs.{deal.orig.toLocaleString()}</div>}
                    </div>
                  </div>
                );
              })}
            </div>

            <button className="ph-btn ph-btn-outline" onClick={handleAddToCart}>Add to Cart</button>
            <button className="ph-btn ph-btn-fill" onClick={handleBuyNow}>⚡ Buy It Now</button>

            <div className="ph-cod-note">💵 <strong>Cash on Delivery (COD) </strong> — Pay When Your Order Arrives</div>
             <div className="ph-timeline">
              <div className="ph-step"><span className="ph-dot">🛒</span><span className="ph-step-day">Day 1</span>Ordered</div>
              <div className="ph-step"><span className="ph-dot">📦</span><span className="ph-step-day">Day 2</span>Parcel Ready</div>
              <div className="ph-step"><span className="ph-dot">✅</span><span className="ph-step-day">Day 3 - Day 4</span>Delivered</div>
            </div>

            <div className="product-tabs">
              <button className="product-tab-btn" onClick={() => scrollToAnchor('benefits-anchor')}>Benefits</button>
              <button className="product-tab-btn tab-desktop-only" onClick={() => scrollToAnchor('compare-anchor')}>Features</button>
              <button className="product-tab-btn" onClick={() => scrollToAnchor('faq-anchor')}>FAQ's</button>
            </div>

           
          </div>
        </div>
      </section>

      {/* ABOUT — same text content as before, moved below the simplified buy box.
          Even without a video, the text stays on the LEFT side (not centered). */}
      <section id="elevate-anchor" className={`about-section${aboutHasMedia ? ' about-wide' : ' no-media'}`}>
        <div className={`about-grid${aboutHasMedia ? ' has-media' : ''}`}>
        <div className="about-inner">
          <div className="section-label">What Makes Us Different?</div>
          <h2 className="section-title">{o?.hero_headline || 'ELEVATE YOUR WELLNESS. SLIM NATURALLY!'}</h2>
          <div className="about-text">
            <p>{ 'Organic Slim Fit Tea — Your Natural Path from Fat to Fit Body Transformation  '}</p>
            <p>{ 'Organic Slim Fit Tea is a 100% original herbal formula designed to boost metabolism, target belly fat, support natural detox, and promote a slim & smart lifestyle.'}</p>
            <p>{ 'Powered by an exclusive 8-Ingredient Super Formula, it combines premium herbs including Unoxidized Tea, Fennel Seeds, Carom Seeds, Dried Mint, Ceylon Cinnamon, Premium Cardamom, Black Pepper, and Nigella Seeds.'}</p>
          </div>
        </div>
        <AboutMedia url={o?.about_media_url} type={o?.about_media_type} poster={o?.about_media_poster} duration={o?.about_media_duration} loading={cfgLoading && !o?.about_media_url} />
        </div>
      </section>

      {/* TRUST BADGE STRIP */}
      <div className="trust-badge-strip">
        <div className="trust-badge-pill">🌿 Natural Ingredients</div>
        <div className="trust-badge-pill">🚫 No Artificial Flavors</div>
        <div className="trust-badge-pill">🍃 No Added Sugar</div>
      </div>

    
      {/* HOW TO USE / BREW */}
      <section className="how-to" id="howto-anchor">
        <div className="section-label" style={{ color: 'var(--gold-light)' }}>Brew Goodness. Sip Results.</div>
        <h2 className="section-title">How to Brew?</h2>
        <div className="steps-grid">
          {[
            ['🔥', '1', 'BOIL', 'Boil 1 cup of fresh water'],
            ['🫖', '2', 'STEEP', 'Add tea and pour boiled water. Let it steep 5–7 mins'],
            ['☕', '3', 'POUR', 'Strain and pour into your cup'],
            ['✨', '4', 'ENJOY', 'Best enjoyed 30 minutes after meals'],
          ].map(([icon, num, title, desc]) => (
            <div key={num} className="step">
              <div className="brew-icon-circle">{icon}</div>
              <div className="step-num">{num}</div>
              <div className="step-title">{title}</div>
              <div className="step-desc">{desc}</div>
            </div>
          ))}
        </div>
        <div className="serve-with-wrap">
          <div className="serve-with-title">As per your taste, you can add (optional)</div>
          <div className="serve-with-pills">
            <div className="serve-with-pill"><span className="icon">🍋</span>Lemon</div>
            <div className="serve-with-pill"><span className="icon">🍯</span>Honey</div>
            <div className="serve-with-pill"><span className="icon">🫚</span>Ginger</div>
          </div>
        </div>
      </section>

      {/* BENEFITS — Nature's Secret to a Lighter You */}
      <section className="benefits-section" id="benefits-anchor">
        <div className="section-label">Sip Smart. Burn Fat. Feel Amazing.</div>
        <h2 className="section-title" style={{ textAlign: 'center' }}>Nature's Secret to a Lighter You</h2>
        <p className="section-desc" style={{ textAlign: 'center', marginBottom: 30 }}>{o?.product_title || 'Organic Slim Fit Tea'} — Natural Fat Loss Tea</p>
        <div className="benefits-inner">
          <div className="benefits-list-col">
            {[
              ['🔥', 'Boosts Metabolism', 'Helps your body burn calories more efficiently.'],
              ['🏃', 'Improves Energy Level', 'Fights fatigue and keeps you active throughout the day.'],
              ['🌬️', 'Reduces Bloating', 'Helps reduce bloating and supports a flatter, lighter feeling.'],
              ['💧', 'Reduces Water Retention', 'Helps flush out excess water and reduces puffiness.'],
              ['♻️', 'Improves Digestion', 'Supports healthy digestion and nutrient absorption.'],
            ].map(([icon, title, desc], i) => (
              <div key={title} className="benefit-row">
                <div className="benefit-num">{i + 1}</div>
                <div>
                  <div className="benefit-title"><span className="benefit-icon">{icon}</span>{title}</div>
                  <div className="benefit-desc">{desc}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="benefits-visual">
              <img src={'/images/5.webp'} alt="Organic Slim Fit Tea" loading="lazy" decoding="async" width="920" height="920" />
          </div>
        </div>
      </section>

      {/* INGREDIENTS */}
      <section className="ingredients-section" id="ingredients-anchor">
        <div className="section-label">What's Inside</div>
        <h2 className="section-title">{o?.ingredients_title || '8 Premium Natural Ingredients'}</h2>
        <p className="section-desc">Each ingredient is carefully selected for maximum effectiveness</p>
        <div className="ingredients-grid">
          {ingredients.map((ing, i) => (
            <div key={ing.name || i} className="ingredient-card">
              <div className="ing-num">{i + 1}</div>
              <div className="ing-icon"><IngredientIcon name={ing.name} fallback={ing.icon} /></div>
              <div className="ing-name">{ing.name}</div>
              <div className="ing-desc">{ing.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* COMPARISON TABLE — Why Choose Us */}
      <section className="compare-section" id="compare-anchor">
        <div className="section-label" style={{ textAlign: 'center' }}>The Difference</div>
        <h2 className="section-title" style={{ textAlign: 'center' }}>
          Why Choose <span style={{ color: 'var(--deep-green)' }}>Organic Slim Fit</span>
        </h2>
        <p className="section-desc" style={{ textAlign: 'center' }}>Not all wellness teas are equal. Here's how we stack up against what else is out there.</p>
        <div className="compare-table-wrap">
          <table className="compare-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th className="compare-brand-col">
                  <div className="compare-recommended-tag">Recommended</div><br />
                  Organic Slim Fit
                </th>
                <th>Market Teas</th>
                <th>Diet Pills</th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row) => (
                <tr key={row}>
                  <td>{row}</td>
                  <td className="compare-brand-col"><span className="compare-check on-brand">✓</span></td>
                  <td><span className="compare-cross">✕</span></td>
                  <td><span className="compare-cross">✕</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section" id="faq-anchor">
        <div className="section-label" style={{ textAlign: 'center' }}>Questions We Actually Get Asked</div>
        <h2 className="section-title" style={{ textAlign: 'center' }}>Frequently Asked Questions</h2>
        <div className="faq-list">
          {faqs.map((f, i) => (
            <div key={f.q} className={`faq-item ${openFaq === i ? 'open' : ''}`}>
              <button className="faq-question" onClick={() => setOpenFaq(openFaq === i ? -1 : i)}>
                {f.q}
                <span className="faq-plus">+</span>
              </button>
              <div className="faq-answer"><p>{f.a}</p></div>
            </div>
          ))}
        </div>
      </section>

      {/* REVIEWS */}
      <section className="reviews" id="reviews-anchor">
        <div className="section-label">Real Results</div>
        <h2 className="section-title">What Our Customers Say</h2>
        <p className="section-desc">{'10,000+'} Happy Customers Across Pakistan</p>
        <div className="reviews-marquee">
          <div className="reviews-track" ref={reviewsTrackRef}>
            {[...reviews, ...reviews].map((r, i) => (
              <div key={i} className="review-card">
                <div className="review-stars">★★★★★</div>
                <p className="review-text">"{r.text}"</p>
                <div className="review-author">{r.author} — {r.city}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER CTA */}
      <div className="cod-banner" style={{ marginTop: 0 }}>
        <h3>{o?.footer_cta_title || 'Ready to Start Your Journey? 🌿'}</h3>
        <p>Order Today - Cash on Delivery available across Pakistan</p>
        <div style={{ marginTop: 20 }}>
          <button className="hero-cta" onClick={() => { setCheckoutFlow('choose'); quickBuy(); }} style={{ margin: '0 auto', display: 'inline-flex' }}>
            <span className="t1">Order Now</span>
          </button>
        </div>
      </div>
    </>
  );
}
